import { useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert, Spinner } from "react-bootstrap";
// import authApi from "services/authApi";
import http from "services/httpClient";

interface Label {
  id: number;
  label: string;
  unit: string;
  column_index: number;
  is_enabled: boolean;
  multiplier: number;
  offset: number;
}

interface Props {
  show: boolean;
  onHide: () => void;
  deviceId: number;
}

export default function EditDeviceParamsModal({ show, onHide, deviceId }: Props) {
  const [labels, setLabels] = useState<Label[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (deviceId && show) {
      setLoading(true);
      http
        .get(`/device/${deviceId}/labels`)
        .then((res) => {
          setLabels(res.data);
          setError(null);
        })
        .catch(() => setError("Failed to load labels"))
        .finally(() => setLoading(false));
    }
  }, [deviceId, show]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await http.put(`/device/${deviceId}/labels`, labels);
      onHide();
    } catch (err) {
      setError("Failed to save labels.");
      // console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const updateLabel = <K extends keyof Label>(index: number, field: K, value: Label[K]) => {
    const updated = [...labels];
    updated[index] = { ...updated[index], [field]: value };
    setLabels(updated);
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Parameters</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" />
          </div>
        ) : (
          <>
            <Row className="mb-2 fw-bold text-muted">
              <Col md={3}>Label</Col>
              <Col md={2}>Unit</Col>
              <Col md={2}>Multiplier</Col>
              <Col md={2}>Offset</Col>
              <Col md={1}>Enable</Col>
            </Row>
            {labels.map((label, index) => (
              <Row key={label.id} className="mb-2">
                <Col md={3}>
                  <Form.Control
                    type="text"
                    value={label.label}
                    onChange={(e) => updateLabel(index, "label", e.target.value)}
                    placeholder={`Param ${label.column_index}`}
                  />
                </Col>
                <Col md={2}>
                  <Form.Control
                    type="text"
                    value={label.unit}
                    onChange={(e) => updateLabel(index, "unit", e.target.value)}
                    placeholder="Unit"
                  />
                </Col>                
                <Col md={2}>
                  <Form.Control
                    type="number"
                    step="any"
                    value={label.multiplier}
                    onChange={(e) => updateLabel(index, "multiplier", parseFloat(e.target.value) || 0)}
                  />
                </Col>
                <Col md={2}>
                  <Form.Control
                    type="number"
                    step="any"
                    value={label.offset}
                    onChange={(e) => updateLabel(index, "offset", parseFloat(e.target.value) || 0)}
                  />
                </Col>
                <Col md={1}>
                  <Form.Check
                    type="checkbox"
                    label="Enabled"
                    checked={label.is_enabled}
                    onChange={(e) => updateLabel(index, "is_enabled", e.target.checked)}
                  />
                </Col>
              </Row>
            ))}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={saving}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
